import React, { useState } from "react";
import { verifyAccount, setToken } from "../api";
import { ageFromDob, todayIso } from "../utils/age";
import { track } from "../utils/analytics";

function VerifyPage({ verifyToken, onVerified }) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [gender, setGender] = useState("");
  const [dob, setDob] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!gender) {
      setError("Please select your gender.");
      return;
    }
    if (!dob) {
      setError("Please enter your date of birth.");
      return;
    }
    if (ageFromDob(dob) < 13) {
      setError("You must be at least 13 years old to register.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const data = await verifyAccount({ token: verifyToken, password, gender, date_of_birth: dob });
      setToken(data.token);
      track("signup", { method: "email" });
      onVerified();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dark-scope min-h-screen flex items-center justify-center px-4" style={{ background: "var(--ink)" }}>
      <div className="bg-white rounded-xl shadow-md p-8 w-full max-w-sm">
        <h1 className="text-3xl font-bold text-emerald-700 mb-1 font-arabic">مراجعة</h1>
        <h2 className="text-lg font-semibold text-slate-700 mb-6">Verify your email and set a password</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Gender <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-slate-400 mb-2">
              Practice calls are gender-segregated. This cannot be changed after signup.
            </p>
            <div className="flex gap-3">
              {[{ value: "male", label: "Male" }, { value: "female", label: "Female" }].map(({ value, label }) => (
                <label
                  key={value}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm font-medium transition-colors ${
                    gender === value
                      ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                      : "border-slate-300 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  <input
                    type="radio"
                    name="gender"
                    value={value}
                    checked={gender === value}
                    onChange={() => setGender(value)}
                    className="sr-only"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Date of birth <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-slate-400 mb-2">
              Some features (such as practice calls) may require you to be 18 or over.
            </p>
            <input
              type="date"
              required
              value={dob}
              max={todayIso()}
              onChange={(e) => setDob(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Password
            </label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Choose a password"
              className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Confirm password
            </label>
            <input
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat your password"
              className="w-full px-3 py-2 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 rounded-md bg-emerald-600 text-white font-medium text-sm hover:bg-emerald-700 disabled:opacity-50"
          >
            {loading ? "Verifying…" : "Activate account"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default VerifyPage;
